import { getEntityFriendlyName } from "../../utils/entityFriendlyName";
import { badgeClassName } from "../../elements/badgeClassName";
import { ReactNode } from "react";
import { getFieldComponent } from "../../config/fieldComponents";
import {
  convertUTCDateTimeToLocal,
  toDisplayDate,
  toDisplayDateTime,
} from "../fields/formatDate";
import {
  TableColumnConfig,
  TableColumnDisplay,
} from "../../config/tableConfig.types";

const isTrue = (value: unknown) => value === true || String(value) === "true";

const toYesNo = (value: unknown) => (isTrue(value) ? "Yes" : "No");

// For "do not ..." flags, shown as the consent they imply. Blank stays blank.
const toInvertedYesNo = (value: unknown) => {
  if (value === null || value === undefined || value === "") {
    return "";
  }

  return isTrue(value) ? "No" : "Yes";
};

const poundsFormat = new Intl.NumberFormat("en-GB", {
  style: "currency",
  currency: "GBP",
});

// Blank stays blank rather than reading as £0.00.
const toMoney = (value: unknown) => {
  if (value === null || value === undefined || value === "") {
    return "";
  }

  const amount = Number(value);

  return Number.isNaN(amount) ? String(value) : poundsFormat.format(amount);
};

// A lookup arrives as { Id, Name }, and its name is what a cell shows.
const toText = (value: unknown) => {
  if (value === null || value === undefined) {
    return "";
  }

  return typeof value === "object" && "Name" in value
    ? String((value as { Name: unknown }).Name ?? "")
    : String(value);
};

type DisplayRenderer = (
  value: unknown,
  column: TableColumnConfig,
  row?: Record<string, unknown>
) => ReactNode;

/**
 * Config names a format; this holds the renderer for it. Adding a format is one
 * entry here, and a stored config can never carry code.
 */
const Displays: Record<TableColumnDisplay, DisplayRenderer> = {
  text: (value) => toText(value),
  date: (value) => toDisplayDate(value as string),
  // CRM sends date-times in UTC with no zone.
  datetime: (value) =>
    toDisplayDateTime(convertUTCDateTimeToLocal(value as string)?.toDate()),
  "local-datetime": (value) => toDisplayDateTime(value as string),
  money: (value) => toMoney(value),
  badge: (value, column, row) => {
    const text = toText(value);
    const variant = column.variantKey ? toText(row?.[column.variantKey]) : "";

    return text ? (
      <span className={badgeClassName(variant)}>{text}</span>
    ) : (
      ""
    );
  },
  yesno: (value) => toYesNo(value),
  "yesno-badge": (value) => (
    <span className={badgeClassName(isTrue(value) ? "success" : "info")}>
      {toYesNo(value)}
    </span>
  ),
  "inverted-yesno": (value) => toInvertedYesNo(value),
  lowercase: (value) => toText(value).toLowerCase(),
  "entity-name": (value) => getEntityFriendlyName(toText(value)),
  // An unmatched value shows as it is, so a new option never leaves a blank cell.
  // Some services send an option set as { Id, Name } rather than the bare value.
  picklist: (value, column) => {
    const reference =
      typeof value === "object" && value !== null
        ? (value as { Id?: unknown; Name?: unknown })
        : undefined;
    const key = reference ? reference.Id : value;

    return (
      column.options?.find((option) => String(option.Key) === String(key))
        ?.Value ?? toText(reference ? reference.Name : value)
    );
  },
  // The escape hatch for a cell no format describes, keyed through the same registry a
  // `component` field uses. The row is passed so a cell can act on its record.
  component: (value, column, row) => {
    const Component = getFieldComponent(column.componentKey);

    return Component ? <Component value={value} row={row} /> : toText(value);
  },
};

/**
 * The renderer a column asks for. An unknown name falls back to the raw value, so a
 * stored config cannot break a page by naming a format a later build removed.
 *
 * "inverted-yesno" is kept for configs that stored it, but no page fed by the record
 * API needs it: `ReverseGDPRValues` has already negated a "do not" flag by the time a
 * row reaches here, so a second inversion would undo it.
 */
export function getColumnDisplay(display: TableColumnDisplay | undefined) {
  return (display && Displays[display]) ?? Displays.text;
}
